import { useCallback, useEffect, useRef, useState } from 'react'
import type Hls from 'hls.js'
import { Loader2, RefreshCw, Video, VideoOff, X } from 'lucide-react'
import { extractErrorMessage, extractHlsUrl, type NetradyneSite } from './NetradyneCameraButton'

/**
 * Live dashcam feed for one truck, as a floating panel -- opened from a LIVE round-trip
 * replay ("where is it now? show me"). Same backend as NetradyneCameraButton:
 * POST /api/netradyne/live-stream proxies Netradyne so the token never reaches the
 * browser, and the answer is an HLS URL played with hls.js (or natively on Safari).
 *
 * Netradyne issues a time-boxed session (duration_ms, clamped server-side to 15 s - 3 min),
 * so the panel shows a countdown and a Reconnect button instead of silently freezing on
 * the last frame. Camera position 0 (road) is the one confirmed against the real API;
 * 1 (cabin) is offered too and simply shows Netradyne's error if that truck has none.
 */

const SESSION_MS = 180_000
const CAMERAS: { id: number; label: string }[] = [
  { id: 0, label: 'Road' },
  { id: 1, label: 'Cabin' },
]

type Phase = 'connecting' | 'playing' | 'error' | 'ended'

export function LiveCameraPanel({
  site, vehicleNumber, onClose,
}: {
  site: NetradyneSite
  vehicleNumber: string
  onClose: () => void
}): JSX.Element {
  const [camera, setCamera] = useState(0)
  const [phase, setPhase] = useState<Phase>('connecting')
  const [error, setError] = useState<string | null>(null)
  const [url, setUrl] = useState<string | null>(null)
  const [endsAt, setEndsAt] = useState<number | null>(null)
  const [now, setNow] = useState(Date.now())
  const [nonce, setNonce] = useState(0)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const hlsRef = useRef<Hls | null>(null)

  const reconnect = useCallback(() => setNonce((n) => n + 1), [])

  // Request a session whenever the truck, camera or a reconnect asks for one.
  useEffect(() => {
    const ctl = new AbortController()
    setPhase('connecting')
    setError(null)
    setUrl(null)
    setEndsAt(null)
    fetch('/api/netradyne/live-stream', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, vehicle_number: vehicleNumber, camera_position: [camera], duration_ms: SESSION_MS }),
      signal: ctl.signal,
    })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}))
        const hls = r.ok ? extractHlsUrl(j) : null
        if (!hls) throw new Error(r.ok ? 'The dashcam returned no playable stream' : extractErrorMessage(j, `HTTP ${r.status}`))
        setUrl(hls)
        setEndsAt(Date.now() + SESSION_MS)
      })
      .catch((e: unknown) => {
        if (ctl.signal.aborted) return
        setError(e instanceof Error ? e.message : String(e))
        setPhase('error')
      })
    return () => ctl.abort()
  }, [site, vehicleNumber, camera, nonce])

  // Attach the stream to the <video>.
  useEffect(() => {
    const video = videoRef.current
    if (!url || !video) return
    const onPlaying = () => setPhase('playing')
    video.addEventListener('playing', onPlaying)
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url
      void video.play().catch(() => undefined)
      return () => {
        video.removeEventListener('playing', onPlaying)
        video.removeAttribute('src')
        video.load()
      }
    }
    let cancelled = false
    void import('hls.js').then(({ default: HlsCtor }) => {
      if (cancelled) return
      if (!HlsCtor.isSupported()) {
        setError('This browser cannot play the live stream')
        setPhase('error')
        return
      }
      hlsRef.current?.destroy()
      const hls = new HlsCtor({ lowLatencyMode: true })
      hlsRef.current = hls
      hls.on(HlsCtor.Events.ERROR, (_e, data) => {
        if (data.fatal) {
          setError('The live stream stopped responding')
          setPhase('error')
        }
      })
      hls.loadSource(url)
      hls.attachMedia(video)
      void video.play().catch(() => undefined)
    })
    return () => {
      cancelled = true
      video.removeEventListener('playing', onPlaying)
      hlsRef.current?.destroy()
      hlsRef.current = null
    }
  }, [url])

  // Session countdown -- a local 1 s display tick while the panel is open, not a fetch.
  useEffect(() => {
    if (!endsAt) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [endsAt])
  const left = endsAt ? Math.max(0, endsAt - now) : null
  useEffect(() => {
    if (left === 0 && phase !== 'error') {
      setPhase('ended')
      hlsRef.current?.destroy()
      hlsRef.current = null
    }
  }, [left, phase])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const mmss = left != null ? `${Math.floor(left / 60000)}:${String(Math.floor((left % 60000) / 1000)).padStart(2, '0')}` : null

  return (
    <div
      role="dialog"
      aria-label={`Live camera · ${vehicleNumber}`}
      className="w-[min(420px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-[rgba(15,23,42,0.12)] bg-[#0B1220] text-white shadow-[0_18px_40px_rgba(15,23,42,0.35)]"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#DC2626] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
          <span className={`h-1.5 w-1.5 rounded-full bg-white ${phase === 'playing' ? 'animate-pulse' : 'opacity-60'}`} />
          Live
        </span>
        <span className="font-mono text-[13px] font-bold">{vehicleNumber}</span>
        <div className="ml-2 flex items-center gap-0.5 rounded-lg bg-white/10 p-0.5" role="group" aria-label="Camera">
          {CAMERAS.map((c) => (
            <button
              key={c.id}
              type="button"
              aria-pressed={camera === c.id}
              onClick={() => setCamera(c.id)}
              className={`rounded-md px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                camera === c.id ? 'bg-white text-[#0B1220]' : 'text-white/70 hover:text-white'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close live camera"
          className="ml-auto flex h-7 w-7 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X size={15} />
        </button>
      </div>

      <div className="relative aspect-video bg-black">
        <video ref={videoRef} className="h-full w-full object-contain" muted playsInline autoPlay />
        {phase === 'connecting' || (url && phase !== 'playing' && phase !== 'error' && phase !== 'ended') ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 text-[12px] text-white/80">
            <Loader2 size={22} className="animate-spin" />
            Connecting to the dashcam…
          </div>
        ) : null}
        {phase === 'error' || phase === 'ended' ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/75 px-6 text-center">
            <VideoOff size={22} className="text-white/70" />
            <p className="text-[12.5px] font-medium">{phase === 'ended' ? 'Live session ended' : 'Live camera unavailable'}</p>
            {phase === 'error' && error ? <p className="text-[11px] text-white/60">{error}</p> : null}
            <button
              type="button"
              onClick={reconnect}
              className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[12px] font-semibold text-[#0B1220] transition-colors hover:bg-white/90"
            >
              <RefreshCw size={13} /> Reconnect
            </button>
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-2 px-3 py-2 text-[11px] text-white/60">
        <Video size={12} />
        <span>Netradyne dashcam · {CAMERAS.find((c) => c.id === camera)?.label.toLowerCase()} camera</span>
        {mmss && phase === 'playing' ? <span className="ml-auto tabular-nums">session ends in {mmss}</span> : null}
        {phase === 'playing' ? (
          <button type="button" onClick={reconnect} className="ml-2 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-white/80 hover:bg-white/10 hover:text-white">
            <RefreshCw size={11} /> Extend
          </button>
        ) : null}
      </div>
    </div>
  )
}
